import { audioCache } from '@/utils/audioCache';
import { DEFAULT_VOICE } from '@/constants/voiceConstants';
import { processTextForLanguage } from '@/components/audio/utils/playerTextProcessing';
import { LanguageMode } from '@/components/audio/types/audioTypes';
import { callFunction } from '@/integrations/supabase/functions';

/* -------------------------------------------------------------------------- */
/*                             Binary-safe TTS core                            */
/* -------------------------------------------------------------------------- */

export type TtsFormat = 'mp3' | 'wav' | 'opus';

function contentTypeFor(format: TtsFormat) {
  switch (format) {
    case 'wav':
      return 'audio/wav';
    case 'opus':
      // OpenAI "opus" is returned as Ogg/Opus container
      return 'audio/ogg';
    default:
      return 'audio/mpeg'; // mp3
  }
}

export interface TtsRequest {
  text: string;
  voice?: string;               // e.g. 'alloy'
  model?: string;               // e.g. 'gpt-4o-mini-tts'
  format?: TtsFormat;           // default 'mp3'
}

/** Low-level: fetch raw audio bytes from the Edge Function and return a Blob. */
export async function getTtsBlob(req: TtsRequest): Promise<Blob> {
  const {
    text,
    voice = DEFAULT_VOICE ?? 'alloy',
    model = 'gpt-4o-mini-tts',
    format = 'mp3',
  } = req;

  if (!text || !text.trim()) {
    throw new Error('TTS text is empty');
  }

  // IMPORTANT: ask the wrapper for ArrayBuffer
  const { data, error, status } = await callFunction<ArrayBuffer>('text-to-speech', {
    as: 'arrayBuffer',
    body: { text, voice, model, format },
  });

  if (error || !data) {
    throw new Error(error?.message ?? `TTS failed (status ${status})`);
  }

  return new Blob([data], { type: contentTypeFor(format) });
}

/** High-level: return a Blob URL suitable for <audio src>. */
export async function getTtsObjectUrl(req: TtsRequest): Promise<string> {
  const blob = await getTtsBlob(req);
  return URL.createObjectURL(blob);
}

/** Revoke a previously created object URL. */
export function revokeObjectUrl(url?: string) {
  if (!url) return;
  try {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  } catch {
    /* no-op */
  }
}

/* -------------------------------------------------------------------------- */
/*                         Public service used by the UI                       */
/* -------------------------------------------------------------------------- */

export interface TTSOptions {
  voice?: string;
  model?: string;
  speed?: number;
  language?: LanguageMode;
}

export class AbielAudioService {
  /** Single entry that actually calls the Edge Function and returns a Blob. */
  private static async callTTSFunction(text: string, options: TTSOptions = {}): Promise<Blob> {
    const language = options.language || 'english';

    console.log('[TTS] Starting TTS generation:', {
      textLength: text?.length ?? 0,
      textPreview: text?.substring?.(0, 100) + (text && text.length > 100 ? '...' : ''),
      voice: options.voice || DEFAULT_VOICE || 'alloy',
      model: options.model || 'gpt-4o-mini-tts',
      language,
    });

    // 1) Process text according to selected language (your existing utility)
    const { processedText, isFinalTextValid } = processTextForLanguage(text, language);
    if (!isFinalTextValid) {
      throw new Error(`No valid content found for ${language} language selection`);
    }

    console.log('[TTS] Text processed for language:', {
      originalLength: text.length,
      processedLength: processedText.length,
      language,
    });

    // 2) Cache key (include language + voice + normalized text)
    const cacheKey = audioCache.generateCacheKey(
      processedText,
      options.voice || DEFAULT_VOICE || 'alloy',
      language
    );

    // 3) Try cache first (URL to previously stored blob)
    const cachedUrl = await audioCache.get(cacheKey);
    if (cachedUrl) {
      console.log('[TTS] Using cached audio URL for:', cacheKey);
      try {
        // Quick validity probe
        const head = await fetch(cachedUrl, { method: 'HEAD' });
        if (head.ok) {
          // Fetch to Blob because this method returns a Blob
          const blobResp = await fetch(cachedUrl);
          const blob = await blobResp.blob();
          if (blob.size > 0) {
            console.log('[TTS] Cached blob retrieved successfully:', { size: blob.size, type: blob.type });
            return blob;
          }
        }
        console.warn('[TTS] Cached URL invalid or empty; generating fresh audio');
      } catch (err) {
        console.warn('[TTS] Cache check failed; generating fresh audio:', err);
      }
    }

    // 4) Generate fresh bytes via Edge Function (binary-safe)
    const started = Date.now();
    try {
      const blob = await getTtsBlob({
        text: processedText,
        voice: options.voice || DEFAULT_VOICE || 'alloy',
        model: options.model || 'gpt-4o-mini-tts',
        format: 'mp3',
      });

      if (!blob || blob.size === 0) {
        throw new Error('TTS generated an empty audio blob');
      }

      console.log(`[TTS] Edge function returned audio in ${Date.now() - started}ms`, {
        size: blob.size,
        type: blob.type,
      });

      // 5) Cache the blob for future use
      try {
        await audioCache.set(cacheKey, blob);
        console.log('[TTS] Cached blob successfully with key:', cacheKey);
      } catch (cacheErr) {
        console.warn('[TTS] Failed to cache blob (non-fatal):', cacheErr);
      }

      return blob;
    } catch (err) {
      console.error('[TTS] Edge function call failed:', err);
      throw err;
    }
  }

  /** Returns a Blob (suitable if the caller wants to manage the URL). */
  static async generateAudioBlob(text: string, options: TTSOptions = {}): Promise<Blob> {
    try {
      const blob = await this.callTTSFunction(text, options);
      return blob;
    } catch (error) {
      console.error('[TTS] generateAudioBlob failed:', error);
      throw error;
    }
  }

  /** Returns a blob URL (convenient for <audio src>). Caller should revoke when done. */
  static async generateAudio(text: string, options: TTSOptions = {}): Promise<string> {
    const language = options.language || 'english';

    // Normalize/prepare and try URL cache first for instant playback
    const { processedText, isFinalTextValid } = processTextForLanguage(text, language);
    if (!isFinalTextValid) {
      throw new Error(`No valid content found for ${language} language selection`);
    }

    const cacheKey = audioCache.generateCacheKey(
      processedText,
      options.voice || DEFAULT_VOICE || 'alloy',
      language
    );

    const cachedUrl = await audioCache.get(cacheKey);
    if (cachedUrl) {
      try {
        const head = await fetch(cachedUrl, { method: 'HEAD' });
        if (head.ok) {
          return cachedUrl; // re-use valid cached URL
        }
      } catch {
        // ignore and fall through to fresh generation
      }
    }

    // Generate new blob and URL
    const blob = await this.callTTSFunction(text, options);
    const url = URL.createObjectURL(blob);

    // Also cache for future plays
    try {
      await audioCache.set(cacheKey, blob);
    } catch {
      /* non-fatal */
    }

    return url;
  }
}
